"use client";
import { useTranslations } from "@zapier/i18n";
import { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { FolderTreeItem, useFolderTree } from "@zapier/client";
import { Text } from "@zapier/design-system";
import { FolderTypeQueryState } from "@zapier/utils";

import FolderTreeBranch from "./FolderTreeBranch";
import { NewFolderRow } from "./NewFolderRow";
import {
  countFolderTreeRows,
  type FolderTreeRowCounts,
} from "./countFolderTreeRows";
import { useFolderTreeKeyboardNavigation } from "../../../hooks/useFolderTreeKeyboardNavigation";
import {
  pinSessionCreatedFolders,
  type SessionCreatedFolder,
} from "./pinSessionCreatedFolders";
import styles from "./FolderTree.module.css";

export type { SessionCreatedFolder } from "./pinSessionCreatedFolders";

export interface PendingNewFolder {
  parentId: string | undefined;
  /** Restores the typed name after a failed create attempt. */
  draftName?: string;
}

/** Inline create-new-folder controls for the move/copy folder tree. */
export interface NewFolderControls {
  pending?: PendingNewFolder;
  onCommit: (name: string) => void;
  onCancel: () => void;
  placeholder: string;
  inputAriaLabel: string;
  /** CAA: keep the input mounted while the create request is in flight. */
  isCreating?: boolean;
}

/** Per-destination disable maps/tooltips for invalid move targets. */
export interface DestinationDisableControls {
  depthDisabledFolderIds?: Set<string>;
  depthDisabledTooltip?: string;
  selectionDisabledFolderIds?: Map<string, string>;
}

/** Session-created folders to pin + optionally delete (CAA Move/Copy pattern). */
export interface SessionCreatedFolderControls {
  folders: SessionCreatedFolder[];
  onDelete: (folderId: string) => void;
  deleteAriaLabel: string;
  isDeleting?: boolean;
}

interface FolderTreeProps {
  disableOrdering?: boolean;
  folderType?: FolderTypeQueryState; // Determines which folders to display (all, personal or shared)
  includeDefaultFolder?: boolean;
  includeTrashFolder?: boolean;
  onItemClick?: (folder: FolderTreeItem) => void;
  searchQuery?: string;
  selectedFolderId?: string;
  /** When set, only folders owned by this user id are shown (plus ancestor rows needed for tree structure). */
  selectedOwner?: number;
  disableSharedFolders?: boolean;
  newFolder?: NewFolderControls;
  destinationDisables?: DestinationDisableControls;
  sessionCreatedFolders?: SessionCreatedFolderControls;
  /** Reports the rows this tree renders, for status announcements. */
  onResultsChange?: (counts: FolderTreeRowCounts) => void;
}

// Recursively collect all folder IDs for initial expansion
const collectAllFolderIds = (folders: FolderTreeItem[]): Set<string> => {
  const ids = new Set<string>();
  const traverse = (folderList: FolderTreeItem[]) => {
    for (const folder of folderList) {
      ids.add(String(folder.id));
      if (folder.children?.length) {
        traverse(folder.children as FolderTreeItem[]);
      }
    }
  };
  traverse(folders);
  return ids;
};

const FolderTreeComponent = ({
  folderType,
  includeDefaultFolder,
  includeTrashFolder,
  onItemClick,
  searchQuery,
  selectedFolderId,
  selectedOwner,
  disableOrdering,
  disableSharedFolders,
  newFolder,
  destinationDisables,
  sessionCreatedFolders,
  onResultsChange,
}: FolderTreeProps) => {
  const t = useTranslations("folder-tree");
  const treeReference = useRef<HTMLUListElement>(null);
  const pendingNewFolder = newFolder?.pending;

  const { folders } = useFolderTree({
    disableOrdering,
    folderType,
    includeDefaultFolder,
    includeTrashFolder,
    searchQuery: searchQuery || "",
    selectedOwner,
  });

  const displayFolders = useMemo(
    () =>
      pinSessionCreatedFolders(folders, sessionCreatedFolders?.folders ?? []),
    [folders, sessionCreatedFolders?.folders],
  );

  const sessionCreatedFolderIds = useMemo(() => {
    const ids = sessionCreatedFolders?.folders.map((folder) =>
      String(folder.id),
    );
    return ids && ids.length > 0 ? new Set(ids) : undefined;
  }, [sessionCreatedFolders?.folders]);

  // Initialize with all folders expanded
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    return collectAllFolderIds(displayFolders);
  });

  useEffect(() => {
    if (!onResultsChange) return;
    onResultsChange(
      countFolderTreeRows(
        displayFolders,
        expandedFolders,
        searchQuery || "",
        disableSharedFolders,
      ),
    );
  }, [
    onResultsChange,
    displayFolders,
    expandedFolders,
    searchQuery,
    disableSharedFolders,
  ]);

  // Stable identity: every row receives this, so a fresh function per render
  // would defeat the memo on the rows.
  const handleToggleExpanded = useCallback((folderId?: string | number) => {
    if (folderId === undefined || folderId === null) return;
    const folderIdString = String(folderId);
    setExpandedFolders((previous) => {
      const next = new Set(previous);
      if (next.has(folderIdString)) {
        next.delete(folderIdString);
      } else {
        next.add(folderIdString);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!pendingNewFolder?.parentId) return;
    setExpandedFolders((previous) => {
      if (previous.has(pendingNewFolder.parentId!)) return previous;
      const next = new Set(previous);
      next.add(pendingNewFolder.parentId!);
      return next;
    });
  }, [pendingNewFolder]);

  const { focusableFolderId, handleKeyDown, getFocusableTabIndex } =
    useFolderTreeKeyboardNavigation({
      folders: displayFolders,
      selectedFolderId,
      expandedFolders,
      onToggleExpanded: handleToggleExpanded,
      onFolderClick: onItemClick,
      treeRef: treeReference,
      isRoot: true,
    });

  // Require an active pending create. `newFolder` controls are always passed
  // from MoveModal, and `pending?.parentId === undefined` is also true when
  // there is no pending object at all — which incorrectly showed the root input.
  const showRootPendingInput =
    pendingNewFolder !== undefined && pendingNewFolder.parentId === undefined;

  return (
    <>
      {displayFolders.length > 0 || showRootPendingInput ? (
        <ul
          ref={treeReference}
          role="tree"
          aria-label={t("a11y.tree-label")}
          className={styles.folderTreeRoot}
        >
          {displayFolders.map((folder, index) => (
            <FolderTreeBranch
              folder={folder}
              folderLevel={0}
              key={folder.id}
              onItemClick={onItemClick}
              searchQuery={searchQuery}
              selectedFolderId={selectedFolderId}
              disabled={disableSharedFolders && folder.isShared}
              expandedFolders={expandedFolders}
              onToggleExpanded={handleToggleExpanded}
              focusableFolderId={focusableFolderId}
              handleKeyDown={handleKeyDown}
              getFocusableTabIndex={getFocusableTabIndex}
              isFirstItem={index === 0}
              newFolder={newFolder}
              destinationDisables={destinationDisables}
              sessionCreatedFolderIds={sessionCreatedFolderIds}
              onDeleteSessionFolder={sessionCreatedFolders?.onDelete}
              deleteSessionFolderAriaLabel={
                sessionCreatedFolders?.deleteAriaLabel
              }
              isDeletingSessionFolder={sessionCreatedFolders?.isDeleting}
            />
          ))}
          {showRootPendingInput && newFolder ? (
            <NewFolderRow
              folderLevel={0}
              onCommit={newFolder.onCommit}
              onCancel={newFolder.onCancel}
              placeholder={newFolder.placeholder}
              draftName={pendingNewFolder?.draftName}
              inputAriaLabel={newFolder.inputAriaLabel}
              isCreating={newFolder.isCreating}
            />
          ) : undefined}
        </ul>
      ) : (
        <div className={styles.noResults}>
          <Text>{t("messages.no-folders-found")}</Text>
        </div>
      )}
    </>
  );
};

// Lets the urgent render pass bail out while a deferred search re-render is
// still pending; without it useDeferredValue costs a pass and saves nothing.
export const FolderTree = memo(FolderTreeComponent);
